import { DatePipe } from "@angular/common";
import {
  endOfDay,
  endOfMonth, endOfWeek,
  endOfYear, endOfYesterday,
  setDay, setMonth, setYear, startOfDay,
  startOfMonth, startOfWeek,
  startOfYear, startOfYesterday
} from 'date-fns';

export class Dates {
  public static readonly SEC: number = 1000;
  public static readonly MIN: number = 1000 * 60;
  public static readonly HOUR: number = 1000 * 60 * 60;
  public static readonly DAY: number = 1000 * 60 * 60 * 24;

  public static readonly FULL_YEAR: string = 'fullYear';
  public static readonly MONTH: string = 'month';
  public static readonly DATE: string = 'date';
  public static readonly HOURS: string = 'hours';
  public static readonly MINUTE: string = 'minutes';
  public static readonly SECONDS: string = 'seconds';
  public static readonly MILLISECONDS: string = 'milliseconds';

  public static readonly OFFSET = new Date().getTimezoneOffset() / 60;

  public static get DATE_FORMAT(): string {
    return 'yyyy-MM-dd';
  }

  public static get DATE_TIME_FORMAT(): string {
    return 'yyyy-MM-dd HH:mm:ss';
  }

  static toString(date: Date, format: string = 'yyyy-MM-dd HH:mm:ss'): any {
    if (date === null) return '';

    return new DatePipe('en_US').transform(date, format);
  }

  static toDateString(date: Date): any {
    return this.toString(date, this.DATE_FORMAT);
  }

  static toDateTimeString(date: Date): any {
    return this.toString(date, this.DATE_TIME_FORMAT);
  }

  static filteredDate(date: any, search: string, format: string = this.DATE_TIME_FORMAT): boolean {
    if (!date) return false;

    if (search.length > 0) {
      let d: string = this.toString(date, format);
      let index: number = d.indexOf(search);
      return index > -1;
    }

    return false;
  }

  static equals(current: Date, other: Date): boolean {
    if (!current && !other) return true;

    if (!current || !other) return false;

    if (!(current instanceof Date && other instanceof Date))
      throw new Error(
        'Can not date equals by values type is not date [type current=' +
          typeof current +
          ', other=' +
          typeof other +
          ']/n[values current=' +
          current +
          ', other=' +
          other +
          ']',
      );

    return this.equalsDateTime(current.getTime(), other.getTime());
  }

  static equalsDateTime(current: number, other: number): boolean {
    if (!current && !other) return true;

    if (!current || !other) return false;

    return current === other;
  }

  static getDiff(minuend: number, subtrahend: number): number {
    return minuend - subtrahend;
  }

  /**
   * 두 날짜 간의 차이를 일수로 반환한다.
   *
   * @param minuend 빼짐을 당할 날짜
   * @param subtrahend 뺄 날짜
   * @return
   */
  static diff(minuend: Date, subtrahend: Date, ms: number = 86400000): number {
    return Math.ceil((minuend.getTime() - subtrahend.getTime()) / ms);
  }

  /**
   * 전달받은 날짜의 시작 시분초를 반환
   * ex) 전달받은날짜가 10월 22일이면, 2020-10-22 00:00:00.000
   * @param d
   */
  static getStartOfDay(d: Date) {
    return startOfDay(d);
  }

  /**
   * 전달받은 날짜의 종료 시분초를 반환
   * ex) 전달받은 날짜가 10월 22일이면, 2020-10-22 23:59:59.999
   * @param d
   */
  static getEndOfDay(d: Date) {
    return endOfDay(d);
  }

  /**
   * 밀리세컨을 포함한 어제날짜의 시작시간을 반환
   * ex) 어제날짜가 10월 21일이면, 2020-10-21 00:00:00.000이 반환됨
   */
  static getStartOfYesterday() {
    return startOfYesterday();
  }

  /**
   * 밀리세컨을 포함한 어제날짜의 마지막 시간을 반환
   * ex) 어제날짜가 10월 21일이면, 2020-10-21 23:59:59.999이 반환됨
   */
  static getEndOfYesterday() {
    return endOfYesterday();
  }

  /**
   * 전달받은 날짜의 주 시작일(일요일)을 반환,
   * 반환되는 값의 시분초는 0시 0분 0.000초 이다.
   * @param
   */
  static getStartOfWeek(d: Date): Date {
    return startOfWeek(d, { weekStartsOn: 1 });
  }

  /**
   * 전달받은 날짜의 주 종료일(토요일)을 반환
   * 반환되는 값의 시분초는 23시 59분 59.999초 이다.
   * @param d
   */
  static getEndOfWeek(d: Date): Date {
    return endOfWeek(d, { weekStartsOn: 1 });
  }

  /**
   * 전달받은 날짜의 월 시작일을 반환
   * 반환되는 값의 시분초는 0시 0분 0.000초 이다.
   * @param d
   */
  static getStartOfMonth(d: Date): Date {
    return startOfMonth(d);
  }

  /**
   * 전달받은 날짜의 월 종료일을 반환
   * 반환되는 값의 시분초는 23시 59분 59.999초 이다.
   * @param d
   */
  static getEndOfMonth(d: Date) {
    return endOfMonth(d);
  }

  /**
   * 전달받은 날짜의 연 시작일을 반환
   * 반환되는 값의 시분초는 0시 0분 0.000초 이다.
   * @param d
   */
  static getStartOfYear(d: Date) {
    return startOfYear(d);
  }

  /**
   * 전달받은 날짜의 연 종료일을 반환
   * 반환되는 값의 시분초는 23시 59분 59.999초 이다.
   * @param d
   */
  static getEndOfYear(d: Date) {
    return endOfYear(d);
  }

  /**
   * 추가일을 포함한 날짜 계산 후 Date 타입으로 반환
   * @param inputDate 날짜
   * @param num 추가일
   */
  public static setAddDateToDate(inputDate: Date, num: number): Date {
    const newDate = new Date(inputDate);
    newDate.setDate(inputDate.getDate() + num);
    return newDate;
  }

  /**
   * 한주의 처음요일 (월요일)을 반환
   * @param date
   */
  public static findWeekMonday(date: Date): Date {
    const zeroDate = this.setZeroBase(date);
    if (zeroDate.getDay() === 1) {
      return zeroDate;
    } else if (zeroDate.getDay() < 1) {
      return this.setAddDateToDate(zeroDate, -6);
    } else {
      return this.setAddDateToDate(zeroDate, zeroDate.getDay() - (zeroDate.getDay() * 2 - 1));
    }
  }

  /**
   * 한주의 마지막요일 (일요일)을 반환
   * @param date
   */
  public static findWeekSunday(date: Date): Date {
    const zeroDate = this.setZeroBase(date);
    if (zeroDate.getDay() === 0) {
      return zeroDate;
    } else {
      return this.setAddDateToDate(this.findWeekMonday(zeroDate), 6);
    }
  }

  /**
   * 0시 0분 0초로 초기화
   * @param date
   */
  public static setZeroBase(date: Date) {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);

    return date;
  }

  /**
   * 23시 59분 59초로 초기화
   * @param date
   */
  public static setNightBase(date: Date) {
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    date.setMilliseconds(59);

    return date;
  }

  // 두개의 날짜를 비교하여 차이를 알려준다. (시간단위 반환)
  public static dateDiffMs(date1, date2) {
    return Math.ceil((date1.valueOf() - date2.valueOf()) / 1000);
  }

  /**
   * 초단위로 전달받은 값을 년/일/시간/분/초 로 반환
   * @param second 초
   * @param limit 표시 제한
   */
  public static dateToString(second, limit) {
    const SEC = 1;
    const MIN = SEC * 60;
    const HOUR = MIN * 60;
    const DAY = HOUR * 24;
    const YEAR = DAY * 365;

    let a: any = [];

    if (!second || second <= 0) return '0' + '초';

    if (second >= YEAR) {
      a.push(Math.floor(Number(second / YEAR)) + '년');
      second = second % YEAR;
    }

    if (second >= DAY) {
      a.push(Math.floor(Number(second / DAY)) + '일');
      second = second % DAY;
    }

    if (second >= HOUR) {
      a.push(Math.floor(Number(second / HOUR)) + '시간');
      second = second % HOUR;
    }

    if (second >= MIN) {
      a.push(Math.floor(Number(second / MIN)) + '분');
      second = second % MIN;
    }

    if (second > 0) a.push(Number(second) + '초');

    if (a.length === 0) return '0' + '초';
    else if (limit > 0 && a.length > limit) a.length = limit;

    return a.join(' ');
  }

  public static fromNow(date: Date, suffix: string = "") {
    if (!date) return '';

    if (typeof date == 'string')
      date = new Date(date);

    var base = new Date();
    var diff = (base.getTime() - date.getTime()) / 1000;
    if (diff < 60) return Math.floor(diff) + ' 초' + suffix;
    if (diff < 3600) return Math.floor(diff / 60) + ' 분' + suffix;
    if (diff < 86400) return Math.floor(diff / 3600) + ' 시간' + suffix;
    if (diff < 604800) return Math.floor(diff / 86400) + ' 일' + suffix;
    if (diff < 2592000) return Math.floor(diff / 604800) + ' 주' + suffix;
    if (diff < 31536000) return Math.floor(diff / 2592000) + ' 달' + suffix;
    return Math.floor(diff / 31536000) + ' 년' + suffix;
  }

  public static ionDate(date) {
    var t = date.split(/[- + : T Z /./]/);
    return new Date(t[0], Number(t[1]) - 1, t[2], Number(t[3]) - Dates.OFFSET, t[4], t[5], t[6] || 0);
  }

  /**
   * * Date 객체를 받아 당일 최소 시간으로 설정하여 반환
   * @param d
   */
  public static morning(d: Date): Date {
    return this.truncate(new Date(d), this.DATE);
  }

  /**
   * Date 객체를 받아 당일 최대 시간으로 설정하여 반환
   * @param d
   */
  public static night(d: Date): Date {
    var nd: Date = new Date(d);
    nd.setHours(23);
    nd.setMinutes(59);
    nd.setSeconds(59);
    nd.setMilliseconds(0);
    return nd;
  }

  /**
   * Date객체를 받아 해당하는 field의 하위 시간을 초기화하여 반환
   * <pre>
   * <li>2018-02-04 11:22:33:444, FULL_YEAR => 2018-01-01 00:00:00:000
   * <li>2018-02-04 11:22:33:444, MONTH => 2018-02-01 00:00:00:000
   * <li>2018-02-04 11:22:33:444, DATE => 2018-02-04 00:00:00:000
   * <li>2018-02-04 11:22:33:444, HOUR => 2018-02-04 11:00:00:000
   * <li>2018-02-04 11:22:33:444, MINUTE => 2018-02-04 11:22:00:000
   * <li>2018-02-04 11:22:33:444, SECOND => 2018-02-04 11:22:33:000
   * <li>2018-02-04 11:22:33:444, MILLISECONDS => 2018-02-04 11:22:33:000
   * <li>2018-02-04 11:22:33:444, null or other => 2018-02-04 11:22:33:444
   * <li>null, null or other => null
   * </pre>
   * @param d Date
   * @param field String Dates.MINUTE ...
   * @return Date
   * */
  public static truncate(d: Date, field: string): Date {
    const date = new Date(d);
    if (!d) return d;

    if (!field) return d;

    if (field === this.FULL_YEAR) {
      date.setMonth(1);
      date.setDate(1);
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
    } else if (field === this.MONTH) {
      date.setDate(1);
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
    } else if (field === this.DATE) {
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
    } else if (field === this.HOURS) {
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
    } else if (field === this.MINUTE) {
      date.setSeconds(0);
      date.setMilliseconds(0);
    } else if (field === this.SECONDS) {
      date.setMilliseconds(0);
    } else if (field === this.MILLISECONDS) {
      date.setMilliseconds(0);
    }

    return date;
  }

  public static isToday(d: Date): boolean {
    if (!d) return true;

    return this.compareDate(d, new Date()) == 0;
  }

  /**
   * 주어진 두 인자를 날짜까지만 비교한다.
   *
   * dateCompare(2018-03-09, 2018-03-09)	= 0
   * dateCompare(2018-03-09, 2018-03-10)	= -1
   * dateCompare(2018-03-10, 2018-03-09)	= 1
   * dateCompare(null, 2018-03-09)  = -1
   * dateCompare(2018-03-10, null)	= 1
   * dateCompare(null, null)	= 0
   *
   * @return 0은 같음, -1은 첫번째가 빠름, 1은 두번째가 빠름
   */
  public static compareDate(d1: Date, d2: Date, diffTime: boolean = false): number {

    if (diffTime) {
      d1 = typeof d1 == 'string' ? new Date(d1) : d1;
      d2 = typeof d1 == 'string' ? new Date(d2) : d2;
    } else {
      d1 = this.morning(typeof d1 == 'string' ? new Date(d1) : d1);
      d2 = this.morning(typeof d2 == 'string' ? new Date(d2) : d2);
    }

    var diff: number = (d1 ? d1.getTime() : 0) - (d2 ? d2.getTime() : 0);

    if (diff < 0) return -1;
    else if (diff == 0) return 0;
    else return 1;
  }

  public static setDate(
    date: Date,
    year?: number,
    month?: number,
    day?: number,
    hour?: number,
    minutes?: number,
    seconds?: number,
    milliseconds?: number,
  ): Date {
    if (date == null || date == undefined) return date;

    if (year) date = setYear(date, year);
    if (month) date = setMonth(date, month);
    if (day) date = setDay(date, day);
    if (hour) date.setHours(hour);
    if (minutes) date.setMinutes(minutes);
    if (seconds) date.setSeconds(seconds);
    if (milliseconds) date.setMilliseconds(milliseconds);

    return date;
  }

  /**
   * 분을 전달 받아 00:00 타입으로 변환
   * @param minute 분
   */
  public static convertMinuteUnits(minute: any) {
    const hh = this.setAddZero(Math.floor(minute / 60));
    const mm = this.setAddZero(Math.floor(minute % 60));
    return `${hh} : ${mm}`;
  }

  /**
   * 1~9까지 숫자 앞에 0을 추가하여 2자리 숫자로 변환
   * @param obj 1~99까지의 숫자
   */
  public static setAddZero(obj: number) {
    return obj > 9 ? obj : `0${obj}`;
  }

  /**
   * 요일 한글 표시
   * @param date
   */
  public static dayTypeToKor(date: Date) {
    switch (date.getDay()) {
      case 0:
        return '일';
      case 1:
        return '월';
      case 2:
        return '화';
      case 3:
        return '수';
      case 4:
        return '목';
      case 5:
        return '금';
      case 6:
        return '토';
    }
  }

  /**
   * 전달받은 Date 를 MM-DD (EE) 형식으로 반환
   * @param date
   */
  public static setMMDDEE(date: Date) {
    return `${this.setAddZero(date.getMonth() + 1)}-${this.setAddZero(date.getDate())} (${this.dayTypeToKor(date)})`;
  }

  /**
   * 날짜를 YYYYMMDD 타입으로 변경
   * @param obj 날짜
   */
  public static setYYMMDD(obj: Date) {
    return `${obj.getFullYear()}${this.setAddZero(obj.getMonth() + 1)}${this.setAddZero(obj.getDate())}`;
  }

  /**
   * 날짜 강제 올림하여 반환
   * */
  public static ceiling(d: Date): Date {
    if (!d) return d;

    const date: Date = Dates.truncate(d, Dates.DATE);

    if (!date) return d;

    return Dates.setAddDateToDate(date, 1);
  }

  /**
   * offset 만큼 날짜/시간을 더한 새 Date 객체를 반환한다.
   * units 은 Date 객체의 속성이다. (fullYear, month, date, hours 등...)
   * @param date
   * @param amount
   * @param units
   * @return
   */
  public static dateAdd(date: Date, amount: number, units: string | 'fullYear' | 'month' | 'date' | 'hours', byReference: boolean = false): Date {
    const result: Date = byReference ? date : new Date(date.getTime());
    result[units] += amount;
    return result;
  }

  public static dateAddByUnit(date: Date, amount: number, units: string | 'fullYear' | 'month' | 'date' | 'hours', byReference: boolean = false): Date {
    const result: Date = byReference ? date : new Date(date.getTime());
    
    switch (units) {
      case 'fullYear':
        result.setFullYear(result.getFullYear() + amount);
        break;
      case 'month':
        result.setMonth(result.getMonth() + amount);
        break;
      case 'date':
        result.setDate(result.getDate() + amount);
        break;
      case 'hours':
        result.setHours(result.getHours() + amount);
        break;
      default:
        throw new Error('Invalid unit type');
    }
    
    return result;
  }

  /**
   * time 형식의 문자열을 Date로 반환하는 메소드
   * ex) parseTimeString(null, "11:00") => 현재날짜 11:00:00
   * ex) parseTimeString(Date(2020-12-12 23:01:03), "11:00") => Date(2020-12-12 11:00:03)
   * ex) parseTimeString(Date(2020-12-25 02:02:00), "11:00") => Date(2020-12-25 11:00:00)
   * @param baseDate 기준이 되는 Date
   * @param time HH:mm 시간 문자열
   *
   */
  public static parseTimeString(time: string, baseDate: Date = null): Date {
    if (!time || time.length == 0) return null;

    let result: Date = baseDate ? baseDate : this.morning(new Date());

    result.setHours(Number(time.slice(0, 2)));
    result.setMinutes(Number(time.slice(3, 5)));
    return result;
  }
}
